import { createSelector } from 'reselect'
import { namespace } from '.'
import { getAllOrNoneStatus } from '../helpers'
import { Security } from '../securities/reducer'
import { getGridViewState, getWatchlistId } from '../securities/selectors'
import { State } from './reducer'
import { WatchList } from './types'
import { getTypedArray } from '../securities/helpers'
import { ALL_WATCHLIST_LABEL, MINE_WATCHLIST_LABEL } from './helpers'

export const getState = (state: { [namespace]: State }) => state[namespace]

const getWatchLists = createSelector([getState], (state) => state?.watchLists)

export const getWatchList = getWatchLists

const emptyWatchlists = getTypedArray<WatchList>()
const priority = ['IG Most Active', 'HY Most Active']

export const getDropdownWatchlists = createSelector(
  [getWatchLists],
  (watchLists) => {
    return (
      watchLists?.toSorted((a, b) => {
        const aP = priority.indexOf(a.name)
        const bP = priority.indexOf(b.name)
        if (aP !== -1 && bP !== -1) return aP - bP
        if (aP !== -1) return -1
        if (bP !== -1) return 1
        return 0
      }) || emptyWatchlists
    )
  }
)

export const getLastChecked = createSelector(
  [getState],
  (state) => (gridIndex: number) => state.lastCheckedSecurity[gridIndex]
)

export const isPending = createSelector([getState], (state) => state.pending)

export const hasError = createSelector([getState], (state) => state.error)

export const getWatchlistDetails = createSelector(
  [getState],
  (state) => (watchlistId: number) => state.watchlistsDetails[watchlistId]
)

export const getDetailsForWatchlist = createSelector(
  [getState],
  (state) => (watchlistId: number) =>
    state.watchLists?.find((list) => list.id === watchlistId)
)

// undefined means the All view and 0 means Mine; neither is a real watchlist id
export const getWatchlistName = createSelector(
  [getWatchlistId, getDetailsForWatchlist],
  (getIdForGrid, getDetails) => (gridIndex: number) => {
    const watchlistId = getIdForGrid(gridIndex)
    if (watchlistId === undefined) return ALL_WATCHLIST_LABEL
    if (watchlistId === 0) return MINE_WATCHLIST_LABEL
    return getDetails(watchlistId)?.name
  }
)

export const getDetailsCallState = createSelector(
  [getState],
  (state) => state.detailsCallState
)

export const getCheckedSecurityIds = createSelector(
  [getState],
  (state) => state.checkedSecurities
)

const emptyCheckedIds = getTypedArray<number>()

export const getCheckedIdsForGrid = createSelector(
  [getCheckedSecurityIds, getGridViewState, getWatchlistId],
  (checkedSecurities, getViewState, getWatchlistIdForGrid) =>
    (gridIndex: number) => {
      const watchlistId = getWatchlistIdForGrid(gridIndex)
      if (watchlistId === undefined) return emptyCheckedIds
      const checked = checkedSecurities[watchlistId]
      if (!checked || checked.size === 0) return emptyCheckedIds
      const viewSecurityIds = getViewState(gridIndex).securityIds
      if (!viewSecurityIds) return emptyCheckedIds
      const filtered = viewSecurityIds.filter((id) => checked.has(id))
      return filtered.length === 0 ? emptyCheckedIds : filtered
    }
)

export const securityIsChecked = createSelector(
  [getCheckedSecurityIds],
  (checkedSecurityIds) =>
    (securityId: Security['id'], watchlistId: WatchList['id']) => {
      return (
        watchlistId !== undefined &&
        checkedSecurityIds.hasOwnProperty(watchlistId) &&
        checkedSecurityIds[watchlistId].has(securityId)
      )
    }
)

export const getCheckedSecuritiesStatus = createSelector(
  [getWatchlistDetails, securityIsChecked],
  (getDetails, isChecked) => (watchlistId: number) => {
    const watchlist = getDetails(watchlistId)
    return watchlist
      ? getAllOrNoneStatus(watchlist?.securityIds, isChecked, watchlist.id)
      : 'none'
  }
)

export const getNumberOfCheckedSecurities = createSelector(
  [getCheckedSecurityIds],
  (checkedSecurities) => (watchlistId: number | undefined) => {
    let retval = 0
    if (
      watchlistId !== undefined &&
      checkedSecurities.hasOwnProperty(watchlistId)
    ) {
      retval = checkedSecurities[watchlistId].size
    }
    return retval
  }
)

export const getDetailsForCurrentWatchlist = createSelector(
  [getWatchlistId, getWatchlistDetails],
  (getWatchlistIdForGrid, getDetails) => (gridIndex: number) => {
    const watchlistId = getWatchlistIdForGrid(gridIndex)
    return watchlistId !== undefined ? getDetails(watchlistId) : undefined
  }
)

export const getAdminWatchlists = createSelector(
  [getState],
  (state) => state.adminWatchlists
)

export const getFailedUpdateSymbols = createSelector(
  [getState],
  (state) => state.failedAdminCusipsAndIsins
)

export const getBackgroundDetails = createSelector(
  [getState],
  (state) => state.backgroundDetails
)
